import { SUB_FUNCTION_SORT_KEYS, type SubFunctionListItem, type SubFunctionSortKey } from "@seagames/contracts";
import { type ColumnDef, DataTable, Icons, ListHeader, ListTag, ListToolbar, PrimaryCell, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CategoryBanner } from "../../components/access-codes/category-banner";
import { CategoryRules } from "../../components/access-rules/category-rules";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { EmptyCard } from "../../components/common/empty-card";
import { timestampColumn, usageColumn } from "../../components/common/list-columns";
import { type SubFunctionFormTarget, SubFunctionFormModal } from "../../components/functions/sub-function-form-modal";
import { usePageBar } from "../../components/shell/page-bar";
import { errorMessage } from "../../lib/api/messages";
import { deleteSubFunction, invalidateFunctions, mainFunctionDetailOptions, subFunctionsListOptions } from "../../lib/functions/queries";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";

export const Route = createFileRoute("/_app/settings/functions/$mainFunctionId")({
	validateSearch: listSearch(SUB_FUNCTION_SORT_KEYS),
	component: SubFunctionsPage,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface SubFunctionFilters extends Record<string, unknown> {
	search: string;
}
const EMPTY_FILTERS: SubFunctionFilters = { search: "" };

function SubFunctionsPage() {
	const { t } = useTranslation("functions");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const { session } = Route.useRouteContext();
	const { mainFunctionId } = Route.useParams();
	const search = Route.useSearch();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<SubFunctionFormTarget | null>(null);

	const detail = useQuery(mainFunctionDetailOptions(mainFunctionId));
	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) => subFunctionsListOptions({ ...q, main_function_id: mainFunctionId }),
		columnsKey: "sub-functions",
		filters: { empty: EMPTY_FILTERS, fromUrl: (s) => ({ search: s.search ?? "" }), toUrl: (f) => ({ search: f.search.trim() || undefined }) },
		summary: { range: (v) => t("pagination.subSummary", v), empty: t("pagination.subEmpty") },
	});
	const del = useConfirmDelete<SubFunctionListItem>({
		remove: (r) => deleteSubFunction(r.sub_function_id),
		invalidate: invalidateFunctions,
		deleted: (r) => t("subDelete.deleted", { name: r.sub_function_name }),
	});
	const main = detail.data;

	// The top bar carries the category (its card banner + name) and the way back; no in-page title.
	usePageBar({
		title: main ? (
			<span className="inline-flex min-w-0 items-center gap-[0.5rem]">
				<CategoryBanner code={main.main_function_code} cardColor={main.card_color} inkColor={main.card_ink_color} />
				<span className="truncate">{main.main_function_name}</span>
			</span>
		) : undefined,
		subtitle: t("detail.subtitle"),
		back: { to: "/settings/functions", label: t("detail.backToFunctions") },
	});

	const total = page.list.data?.total;
	const sortValue = `${search.sort ?? "code"}:${search.dir ?? "asc"}`;
	const sortOptions = [
		{ value: "code:asc", label: t("drilldown.sortCodeAsc") },
		{ value: "code:desc", label: t("drilldown.sortCodeDesc") },
		{ value: "name:asc", label: tc("sort.nameAsc") },
		{ value: "name:desc", label: tc("sort.nameDesc") },
		{ value: "created_at:desc", label: tc("sort.newest") },
		{ value: "created_at:asc", label: tc("sort.oldest") },
	];

	const columns: ColumnDef<SubFunctionListItem, SubFunctionSortKey>[] = [
		{
			key: "name",
			header: t("subTable.name"),
			sortable: true,
			lockVisible: true,
			minWidth: "14rem",
			// The name bold, the code as the grey sub-line — the list pages' identifying cell.
			cell: (r) => <PrimaryCell sub={r.sub_function_code}>{r.sub_function_name}</PrimaryCell>,
		},
		{
			key: "requires_sport" as SubFunctionSortKey,
			header: t("subTable.requiresSport"),
			minWidth: "8rem",
			cell: (r) =>
				r.requires_sport ? (
					<ListTag title={t("subTable.requiresSportTitle")}>{t("subTable.sportRequired")}</ListTag>
				) : (
					<span className="text-base-content/30">—</span>
				),
		},
		usageColumn("cards_count", t("subTable.cards"), t("subTable.cardsTitle")),
		usageColumn("scopes_count", t("subTable.scopes"), t("subTable.scopesTitle"), "7rem"),
		timestampColumn("created_at", t("subTable.created"), true),
	];

	if (detail.isPending) {
		return (
			<div className="flex flex-col items-center gap-[0.75rem] py-[4rem]">
				<span className="loading loading-spinner loading-md text-gms-ink-3" />
				<p className="text-[0.8125rem] text-gms-ink-3">{tc("state.loading")}</p>
			</div>
		);
	}

	if (detail.isError || main === undefined) {
		return (
			<EmptyCard
				tone="bad"
				icon={<Icons.Alert />}
				title={t("detail.loadFailedTitle")}
				actions={
					<>
						<button type="button" className="btn btn-line btn-sm" onClick={() => void detail.refetch()}>
							{tc("action.retry")}
						</button>
						<Link to="/settings/functions" className="btn btn-line btn-sm">
							{t("detail.backToFunctions")}
						</Link>
					</>
				}
			>
				{detail.isError ? errorMessage(detail.error) : undefined}
			</EmptyCard>
		);
	}

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{/* One compact line under the bar, like her "327 records": the organisation type and where it sits. */}
			<p className="flex flex-wrap items-center gap-x-[0.5rem] gap-y-[0.25rem] text-[0.875rem] leading-normal text-gms-ink-2">
				{main.organization_type && <ListTag>{main.organization_type.name}</ListTag>}
				<span>
					{main.main_function_category !== null
						? t("detail.metaWithCategory", { category: main.main_function_category, order: main.sort_order })
						: t("detail.meta", { order: main.sort_order })}
				</span>
			</p>

			<CategoryRules key={main.main_function_id} mainFunctionId={main.main_function_id} />

			<ListHeader
				as="h2"
				title={t("drilldown.subFunctions")}
				count={total === undefined ? undefined : t("drilldown.count", { count: total, n: formatNumber(total) })}
				actionLabel={t("detail.addSubFunction")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
				className="pt-[0.25rem]"
			/>

			{/* No chips: the sub-function list API has no requires_sport filter. */}
			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("drilldown.searchPlaceholder")}
				sort={{
					value: sortValue,
					options: sortOptions,
					onChange: (v) => {
						// The options never offer main_function (one parent here), and the URL merges the parent list's sort keys.
						const [sort, dir] = v.split(":") as [Exclude<SubFunctionSortKey, "main_function">, "asc" | "desc"];
						page.patch({ sort, dir });
					},
				}}
				onClearAll={page.clearFilters}
			/>

			<DataTable<SubFunctionListItem, SubFunctionSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.sub_function_id}
				empty={page.emptyState({
					filtered: { title: t("empty.subFilteredTitle"), description: t("empty.subFilteredDescription") },
					none: { title: t("empty.subNoneTitle"), description: t("empty.subNoneDescription", { name: main.main_function_name }) },
					clearLabel: t("drilldown.clearSearch"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("subTable.editLabel", { name: r.sub_function_name })}
									onEdit={() => setForm({ mode: "edit", subFunction: r })}
									deleteLabel={t("subTable.deleteLabel", { name: r.sub_function_name })}
									onDelete={() => del.ask(r)}
									canDelete={r.cards_count === 0 && r.scopes_count === 0}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <SubFunctionFormModal target={form} mainFunction={main} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("subDelete.title")}
					description={<Trans t={t} i18nKey="subDelete.description" values={{ name: del.row?.sub_function_name ?? "" }} components={{ strong: <strong className="font-semibold text-base-content" /> }} />}
					confirmLabel={t("subDelete.confirm")}
					errorPrefix={t("subDelete.failed")}
				/>
			)}
		</div>
	);
}
